import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import API from '../../../utils/api';
import { useApp } from '../../../context/AppContext';
import { Card, SectionHead, Btn, Badge, Table, Tr, Td, Modal, FormGroup, FormGrid, Input, CustomSelect, Textarea, selectChevron } from '../../common/UI';
import { generateFleetPDF, generateFleetJobSlipPDF, generateFleetTruckReportPDF, generateOpsStatusReportPDF } from '../../../utils/pdfGenerator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const emptyJobForm = {
  job_date: new Date().toISOString().split('T')[0],
  customer_id: '', vessel_id: '', vessel_name: '', stock_entry_id: '',
  collection_location: '', collection_time: '',
  loading_time: '', truck_number: '', dropoff_location: '', cargo_description: '', dimensions: '', remarks: '',
  job_type: 'fleet', link_fleet: false
};

const STATUS_LABELS = { in_progress: 'In Progress', pending: 'Pending', completed: 'Completed' };

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

/* 24-hour Hour/Minute picker — stores and displays value as 24hr "HH:MM" */
function TimePicker({ value, onChange }) {
  let hour = '09', minute = '00';
  if (value) {
    const [h, m] = value.split(':');
    hour = h;
    minute = m;
  }

  const emit = (h, m) => onChange(`${h}:${m}`);

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <CustomSelect value={hour} onChange={e => emit(e.target.value, minute)} style={{ flex: 1 }}>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </CustomSelect>
      <CustomSelect value={minute} onChange={e => emit(hour, e.target.value)} style={{ flex: 1 }}>
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </CustomSelect>
    </div>
  );
}

/* Single-line truncated text with a click-to-expand toggle for long addresses */
function TruncatedCell({ text, maxWidth = 160 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <>—</>;
  const isLong = text.length > 24;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
      <span style={expanded
        ? { whiteSpace: 'normal', wordBreak: 'break-word' }
        : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth, display: 'inline-block' }}
        title={isLong ? text : undefined}>
        {text}
      </span>
      {isLong && (
        <button type="button" onClick={() => setExpanded(e => !e)}
          style={{ border: 'none', background: 'none', color: '#1e3a8a', cursor: 'pointer', fontSize: 11, fontWeight: 700, flexShrink: 0, padding: 0 }}>
          {expanded ? 'Less' : 'More'}
        </button>
      )}
    </div>
  );
}

/* Styled combobox: pick from known locations OR type a new one — matches the app's other dropdowns */
function LocationCombobox({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => !value || o.toLowerCase().includes(value.toLowerCase()));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Input
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onClick={() => setOpen(true)}
        placeholder={placeholder}
        style={selectChevron}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8,
          boxShadow: '0 6px 16px rgba(0,0,0,.12)', zIndex: 50, maxHeight: 180, overflowY: 'auto'
        }}>
          {filtered.map(opt => (
            <div key={opt} onMouseDown={() => { onChange(opt); setOpen(false); }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Vessel combobox — pick an existing vessel (sets vessel_id) OR type a new name (sets vessel_name) */
function VesselCombobox({ vesselId, vesselName, vessels, onSelectVessel, onTypeName }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayValue = vesselId ? (vessels.find(v => String(v.id) === String(vesselId))?.name || '') : (vesselName || '');
  const filtered = vessels.filter(v => !displayValue || v.name.toLowerCase().includes(displayValue.toLowerCase()));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Input
        value={displayValue}
        onChange={e => { onTypeName(e.target.value); setOpen(true); }}
        onClick={() => setOpen(true)}
        placeholder="Vessel name"
        style={selectChevron}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8,
          boxShadow: '0 6px 16px rgba(0,0,0,.12)', zIndex: 50, maxHeight: 180, overflowY: 'auto'
        }}>
          {filtered.map(v => (
            <div key={v.id} onMouseDown={() => { onSelectVessel(v); setOpen(false); }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              {v.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTime(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function FleetManagement() {
  const { customers, vessels, trucks, showToast, role } = useApp();
  const canSeeCommercials = role !== 'fleet_controller';
  const canEditJobDetails = role === 'fleet_controller' || role === 'admin';
  const canAddJob = role === 'fleet_controller' || role === 'warehouse' || role === 'admin';
  const isFleetRole = role === 'fleet_controller' || role === 'admin';
  const isAdmin = role === 'admin';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null); // null = creating a new job; otherwise editing this job id
  const [form, setForm] = useState(emptyJobForm);
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ invoice_number: '', invoice_value: '', remarks: '' });
  const [remarksTarget, setRemarksTarget] = useState(null);
  const [remarksValue, setRemarksValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Search by truck/vehicle number, customer name, or vessel name
  const filteredJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(j => {
      const truck = (j.truck_number || '').toLowerCase();
      const customer = (j.Customer?.company_name || j.Customer?.company || '').toLowerCase();
      const vessel = (j.Vessel?.name || j.vessel_name || '').toLowerCase();
      return truck.includes(q) || customer.includes(q) || vessel.includes(q);
    });
  }, [jobs, searchQuery]);

  // Jobs within the selected From/To date range — used for exports/reports only
  const reportJobs = useMemo(() => {
    if (!reportFrom && !reportTo) return jobs;
    return jobs.filter(j => {
      if (reportFrom && j.job_date < reportFrom) return false;
      if (reportTo && j.job_date > reportTo) return false;
      return true;
    });
  }, [jobs, reportFrom, reportTo]);

  // Dropdown suggestions built from locations already used on past jobs — typing a new value is still allowed.
  const collectionLocationOptions = useMemo(() =>
    [...new Set(jobs.map(j => j.collection_location).filter(Boolean))].sort(), [jobs]);
  const dropoffLocationOptions = useMemo(() =>
    [...new Set(jobs.map(j => j.dropoff_location).filter(Boolean))].sort(), [jobs]);

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await API.get('/fleet');
      setJobs(data);
    } catch (err) {
      showToast('Error loading fleet jobs', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const openCreate = () => {
    setEditId(null);
    // Fleet Controller's own jobs default to "Fleet Job"; Operations defaults to "Ops Job" —
    // either can still switch the radio before creating.
    setForm({ ...emptyJobForm, job_type: role === 'warehouse' ? 'ops' : 'fleet' });
    setShowCreate(true);
  };

  const openEdit = (job) => {
    setEditId(job.id);
    setForm({
      job_date: job.job_date || new Date().toISOString().split('T')[0],
      customer_id: job.customer_id || '',
      vessel_id: job.vessel_id || '',
      vessel_name: job.vessel_name || '',
      stock_entry_id: job.stock_entry_id || '',
      collection_location: job.collection_location || '',
      collection_time: job.collection_time || '',
      loading_time: job.loading_time || '',
      truck_number: job.truck_number || '',
      dropoff_location: job.dropoff_location || '',
      cargo_description: job.cargo_description || '',
      dimensions: job.dimensions || '',
      remarks: job.remarks || '',
      job_type: job.job_type || 'fleet',
      link_fleet: !!job.fleet_job_number
    });
    setShowCreate(true);
  };

  const handleSaveJob = async () => {
    if (!form.job_date || !form.truck_number || !form.dropoff_location) {
      showToast('Fill all required fields', 'error'); return;
    }
    try {
      if (editId) {
        await API.put(`/fleet/${editId}`, form);
        showToast('✓ Fleet job updated!', 'success');
      } else {
        await API.post('/fleet', form);
        showToast('✓ Fleet job created!', 'success');
      }
      setShowCreate(false);
      setEditId(null);
      setForm(emptyJobForm);
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving fleet job', 'error');
    }
  };

  const handleSaveInvoice = async () => {
    try {
      await API.put(`/fleet/${invoiceTarget.id}/invoice`, invoiceForm);
      showToast('✓ Invoice saved!', 'success');
      setInvoiceTarget(null);
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving invoice', 'error');
    }
  };

  const handleSaveRemarks = async () => {
    try {
      // Only remarks is sent — invoice number/value are left untouched even for a completed job.
      await API.put(`/fleet/${remarksTarget.id}/invoice`, { remarks: remarksValue });
      showToast('✓ Remarks updated!', 'success');
      setRemarksTarget(null);
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error updating remarks', 'error');
    }
  };

  // Fleet Controller's own completion — hands the job off to Operations (status -> pending)
  const handleFleetComplete = async (job) => {
    try {
      await API.put(`/fleet/${job.id}/fleet-complete`);
      showToast('✓ Handed off to Operations!', 'success');
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error completing job', 'error');
    }
  };

  // Operations' final completion (status -> completed)
  const handleComplete = async (job) => {
    try {
      await API.put(`/fleet/${job.id}/complete`);
      showToast('✓ Job marked completed!', 'success');
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error completing job', 'error');
    }
  };

  // Admin-only — full delete, regardless of status
  const confirmDeleteJob = async () => {
    try {
      await API.delete(`/fleet/${deleteTarget.id}`);
      showToast('✓ Job deleted!', 'success');
      setDeleteTarget(null);
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error deleting job', 'error');
    }
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reportJobs.map(j => ({
      Fleet_Job_Number: j.fleet_job_number || '—',
      Ops_Job_Number: j.ops_job_number || '—',
      Job_Type: j.job_type === 'ops' ? 'Ops Job' : 'Fleet Job',
      Date: j.job_date,
      Customer: j.Customer?.company_name || j.Customer?.company || '—',
      Vessel: j.Vessel?.name || j.vessel_name || '—',
      Linked_Stock_PO: j.StockEntry?.po_numbers || '—',
      Collection_Location: j.collection_location || '—',
      Collection_Time: j.collection_time ? formatTime(j.collection_time) : '—',
      Loading_Time: j.loading_time ? formatTime(j.loading_time) : '—',
      Truck_Number: j.truck_number || '—',
      Dropoff_Location: j.dropoff_location || '—',
      Dimensions: j.dimensions || '—',
      Cargo_Description: j.cargo_description || '—',
      Invoice_Number: j.invoice_number || '—',
      Invoice_Value: j.invoice_value || '—',
      Remarks: j.remarks || '—',
      Status: j.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fleet Management');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    saveAs(blob, `AllIn_FleetManagement_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="fade-up page-root">
      <SectionHead title="Fleet Management" subtitle="Track truck loading jobs, invoices and completion">
        {canAddJob && (
          <Btn variant="primary" onClick={openCreate}>
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Job
          </Btn>
        )}
      </SectionHead>

      <div style={{ marginBottom: 12, maxWidth: 360 }}>
        <Input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="🔍 Search by vehicle no., customer or vessel"
        />
      </div>

      <Card style={{ padding: 0 }}>
        <Table
          headers={[
            'Fleet Job #', 'Ops Job #', 'Type', 'Date', 'Customer', 'Vessel',
            'Collection Location', 'Collection Time', 'Loading Time', 'Truck #', 'Drop-off Location', 'Cargo Description',
            ...(canSeeCommercials ? ['Invoice #', 'Invoice Value', 'Remarks'] : []),
            'Status', 'Print', ''
          ]}
          empty={!loading && filteredJobs.length === 0 ? (jobs.length === 0 ? 'No fleet jobs yet.' : 'No jobs match your search.') : null}>
          {filteredJobs.map(job => (
            <Tr key={job.id}>
              <Td mono>{job.fleet_job_number || '—'}</Td>
              <Td mono>{job.ops_job_number || '—'}</Td>
              <Td>{job.job_type === 'ops' ? 'Ops Job' : 'Fleet Job'}</Td>
              <Td>{job.job_date}</Td>
              <Td strong>{job.Customer?.company_name || job.Customer?.company || '—'}</Td>
              <Td>{job.Vessel?.name || job.vessel_name || '—'}</Td>
              <Td><TruncatedCell text={job.collection_location} /></Td>
              <Td>{job.collection_time ? formatTime(job.collection_time) : '—'}</Td>
              <Td>{job.loading_time ? formatTime(job.loading_time) : '—'}</Td>
              <Td mono>{job.truck_number || '—'}</Td>
              <Td><TruncatedCell text={job.dropoff_location} /></Td>
              <Td><TruncatedCell text={job.cargo_description} /></Td>
              {canSeeCommercials && <Td>{job.invoice_number || '—'}</Td>}
              {canSeeCommercials && <Td>{job.invoice_value ? `$${job.invoice_value}` : '—'}</Td>}
              {canSeeCommercials && (
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TruncatedCell text={job.remarks} maxWidth={160} />
                    {/* Once a job is completed, only Admin can still edit remarks — Ops loses access too */}
                    {isAdmin && job.status === 'completed' && (
                      <Btn size="xs" variant="outline" onClick={() => { setRemarksTarget(job); setRemarksValue(job.remarks || ''); }}>✎</Btn>
                    )}
                  </div>
                </Td>
              )}
              <Td><Badge status={job.status}>{STATUS_LABELS[job.status] || job.status}</Badge></Td>
              <Td center>
                <Btn size="xs" variant="outline" onClick={() => generateFleetJobSlipPDF(job)}>🖨 Print</Btn>
              </Td>
              <Td center>
                <div style={{ display: 'grid', gridTemplateColumns: '58px 74px 66px 66px', gap: 6, alignItems: 'center' }}>
                  <div>
                    {((canEditJobDetails && job.status === 'in_progress') || isAdmin) && (
                      <Btn size="xs" variant="outline" onClick={() => openEdit(job)}>✎ Edit</Btn>
                    )}
                  </div>
                  <div>
                    {isFleetRole && job.status === 'in_progress' && (
                      <Btn size="xs" variant="success" onClick={() => handleFleetComplete(job)}>✓ Complete</Btn>
                    )}
                    {canSeeCommercials && job.status === 'pending' && (
                      <Btn size="xs" variant="success" disabled={!job.invoice_number} onClick={() => handleComplete(job)}>✓ Complete</Btn>
                    )}
                  </div>
                  <div>
                    {((canSeeCommercials && job.status !== 'completed') || isAdmin) && (
                      <Btn size="xs" variant="outline" onClick={() => {
                        setInvoiceTarget(job);
                        setInvoiceForm({ invoice_number: job.invoice_number || '', invoice_value: job.invoice_value || '', remarks: job.remarks || '' });
                      }}>🧾 Invoice</Btn>
                    )}
                  </div>
                  <div>
                    {isAdmin && (
                      <Btn size="xs" variant="danger" onClick={() => setDeleteTarget(job)}>🗑 Delete</Btn>
                    )}
                  </div>
                </div>
              </Td>
            </Tr>
          ))}
        </Table>
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>Report range:</span>
          <Input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} style={{ width: 150 }} />
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>to</span>
          <Input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} style={{ width: 150 }} />
          {(reportFrom || reportTo) && (
            <Btn size="xs" variant="outline" onClick={() => { setReportFrom(''); setReportTo(''); }}>✕ Clear</Btn>
          )}
          <div style={{ width: '100%', display: 'flex', gap: 8, marginTop: 4 }}>
            <Btn variant="outline" size="sm" onClick={handleExportExcel}>📊 Export Excel</Btn>
            <Btn variant="primary" size="sm" onClick={() => generateFleetPDF(reportJobs)}>↓ Export PDF</Btn>
            {(role === 'fleet_controller' || role === 'admin') &&
              <Btn variant="outline" size="sm" onClick={() => generateFleetTruckReportPDF(reportJobs)}>🚛 Fleet Report</Btn>}
            {(role === 'warehouse' || role === 'admin') &&
              <Btn variant="outline" size="sm" onClick={() => generateOpsStatusReportPDF(reportJobs)}>📋 Ops Report</Btn>}
          </div>
        </div>
      </Card>

      {/* Create / Edit Job Modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); setEditId(null); }}
        title={editId ? 'Edit Fleet Job' : 'Add New Fleet Job'} icon="🚛"
        footer={<>
          <Btn variant="outline" onClick={() => { setShowCreate(false); setEditId(null); }}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSaveJob}>{editId ? 'Save Changes' : 'Create Job'}</Btn>
        </>}>
        <FormGrid>
          {!editId ? (
            role === 'admin' ? (
              <FormGroup label="Job Type" full>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <input type="radio" name="job_type" checked={form.job_type === 'fleet'}
                      onChange={() => set('job_type', 'fleet')} /> Fleet Job
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <input type="radio" name="job_type" checked={form.job_type === 'ops'}
                      onChange={() => set('job_type', 'ops')} /> Ops Job
                  </label>
                  {form.job_type === 'ops' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginLeft: 10 }}>
                      <input type="checkbox" checked={form.link_fleet}
                        onChange={e => set('link_fleet', e.target.checked)} /> Also create Fleet job (Fleet)
                    </label>
                  )}
                </div>
              </FormGroup>
            ) : (
              <FormGroup label="Job Type" full>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)' }}>
                    {form.job_type === 'ops' ? 'Ops Job' : 'Fleet Job'}
                  </div>
                  {role === 'warehouse' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.link_fleet}
                        onChange={e => set('link_fleet', e.target.checked)} /> Also create Fleet job (Fleet)
                    </label>
                  )}
                </div>
              </FormGroup>
            )
          ) : (
            <FormGroup label="Job Type" full>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)' }}>
                {form.job_type === 'ops' ? 'Ops Job' : 'Fleet Job'}{form.link_fleet && form.job_type === 'ops' ? ' (linked to Fleet)' : ''}
              </div>
            </FormGroup>
          )}
          <FormGroup label="Job Date" required>
            <Input type="date" value={form.job_date} onChange={e => set('job_date', e.target.value)} />
          </FormGroup>
          <FormGroup label="Customer">
            <CustomSelect value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
              <option value="">— Select —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name || c.company}</option>)}
            </CustomSelect>
          </FormGroup>
          <FormGroup label="Vessel">
            <VesselCombobox
              vesselId={form.vessel_id}
              vesselName={form.vessel_name}
              vessels={vessels}
              onSelectVessel={v => setForm(p => ({ ...p, vessel_id: v.id, vessel_name: '' }))}
              onTypeName={text => setForm(p => ({ ...p, vessel_id: '', vessel_name: text }))}
            />
          </FormGroup>
          <FormGroup label="Collection Location">
            <LocationCombobox value={form.collection_location} onChange={v => set('collection_location', v)} options={collectionLocationOptions} placeholder="Pickup location" />
          </FormGroup>
          <FormGroup label="Collection Time">
            <TimePicker value={form.collection_time} onChange={v => set('collection_time', v)} />
          </FormGroup>
          <FormGroup label="Loading Time">
            <TimePicker value={form.loading_time} onChange={v => set('loading_time', v)} />
          </FormGroup>
          <FormGroup label="Truck / Plate Number" required>
            <CustomSelect value={form.truck_number} onChange={e => set('truck_number', e.target.value)}>
              <option value="">— Select Truck —</option>
              {trucks.map(t => <option key={t.id} value={t.truck_number}>{t.truck_number}</option>)}
            </CustomSelect>
          </FormGroup>
          <FormGroup label="Drop-off Location" required full>
            <LocationCombobox value={form.dropoff_location} onChange={v => set('dropoff_location', v)} options={dropoffLocationOptions} />
          </FormGroup>
          <FormGroup label="Cargo Description" full>
            <Textarea value={form.cargo_description} onChange={e => set('cargo_description', e.target.value)} />
          </FormGroup>
          <FormGroup label="Remarks" full>
            <Textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} />
          </FormGroup>
        </FormGrid>
      </Modal>

      {/* Invoice Modal */}
      <Modal open={!!invoiceTarget} onClose={() => setInvoiceTarget(null)}
        title="Add Invoice" icon="🧾"
        footer={<>
          <Btn variant="outline" onClick={() => setInvoiceTarget(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSaveInvoice}>Save Invoice</Btn>
        </>}>
        <FormGrid>
          <FormGroup label="Invoice Number">
            <Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm(p => ({ ...p, invoice_number: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Invoice Value (optional)">
            <Input type="number" value={invoiceForm.invoice_value} onChange={e => setInvoiceForm(p => ({ ...p, invoice_value: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Remarks" full>
            <Textarea value={invoiceForm.remarks} onChange={e => setInvoiceForm(p => ({ ...p, remarks: e.target.value }))} />
          </FormGroup>
        </FormGrid>
      </Modal>

      {/* Remarks-only edit — available even after the job is completed, when the Invoice modal is no longer accessible */}
      <Modal open={!!remarksTarget} onClose={() => setRemarksTarget(null)}
        title="Update Remarks" icon="📝"
        footer={<>
          <Btn variant="outline" onClick={() => setRemarksTarget(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSaveRemarks}>Save</Btn>
        </>}>
        <FormGrid>
          <FormGroup label="Remarks" full>
            <Textarea value={remarksValue} onChange={e => setRemarksValue(e.target.value)} />
          </FormGroup>
        </FormGrid>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Delete Fleet Job" icon="🗑"
        footer={<>
          <Btn variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Btn>
          <Btn variant="danger" onClick={confirmDeleteJob}>Delete</Btn>
        </>}>
        <p style={{ fontSize: 13.5, color: 'var(--text2)', margin: 0 }}>
          Delete fleet job <strong>{deleteTarget?.fleet_job_number || deleteTarget?.ops_job_number || `#${deleteTarget?.id}`}</strong>? This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
